<template>
  <div>
    <b-card>
      <b-alert
        variant="danger"
        :show="!!error"
        dismissible
        @dismissed="error = null"
      >
        <div class="alert-body">
          <p>{{ error }}</p>
        </div>
      </b-alert>

      <div class="d-flex justify-content-between align-items-center mb-1">
        <h4 class="mb-0">
          Ledger Comparison
        </h4>
        <div>
          <b-button
            variant="primary"
            class="mr-1"
            @click="showSupplierSettingsModal = true"
          >
            Supplier Ledger Configs
          </b-button>
        </div>
      </div>

      <b-form @submit.prevent="handleSubmit">
        <b-row>
          <b-col md="6">
            <h5 class="mb-1">
              Supplier Ledger
            </h5>
            <b-card class="mb-1 bg-light">
              <!-- Supplier Select for prepopulation -->
              <b-form-group
                label="Select Supplier (to load saved config)"
                label-for="supplier-select"
              >
                <custom-v-select
                  v-model="selectedSupplier"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="supplierOptions"
                  :loading="supplierOptionsLoading"
                  label="text"
                  placeholder="Select supplier to auto-fill config..."
                  @input="handleSupplierChange"
                />
              </b-form-group>

              <b-form-group
                label="Upload Supplier Ledger"
                label-for="company-ledger"
              >
                <b-form-file
                  id="company-ledger"
                  v-model="form.company_ledger"
                  accept=".xlsx,.xls"
                  placeholder="Choose Excel file or drop it here..."
                  drop-placeholder="Drop file here..."
                  required
                />
              </b-form-group>
              <b-row>
                <b-col md="6">
                  <b-form-group
                    label="Start Row"
                    label-for="company-start-row"
                  >
                    <b-form-input
                      id="company-start-row"
                      v-model.number="form.company_start_row"
                      type="number"
                      min="1"
                      required
                      placeholder="e.g., 2"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group
                    label="End Row"
                    label-for="company-end-row"
                  >
                    <b-form-input
                      id="company-end-row"
                      v-model.number="form.company_end_row"
                      type="number"
                      min="1"
                      required
                      placeholder="e.g., 100"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col md="6">
                  <b-form-group
                    label="Start Column"
                    label-for="company-start-col"
                  >
                    <b-form-input
                      id="company-start-col"
                      v-model="form.company_start_col"
                      required
                      placeholder="e.g., A"
                      maxlength="3"
                      @input="form.company_start_col = uppercaseColumn(form.company_start_col)"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group
                    label="End Column"
                    label-for="company-end-col"
                  >
                    <b-form-input
                      id="company-end-col"
                      v-model="form.company_end_col"
                      required
                      placeholder="e.g., F"
                      maxlength="3"
                      @input="form.company_end_col = uppercaseColumn(form.company_end_col)"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col md="4">
                  <b-form-group
                    label="Date Column"
                    label-for="company-date-col"
                  >
                    <b-form-input
                      id="company-date-col"
                      v-model="form.company_date_col"
                      required
                      placeholder="e.g., B"
                      maxlength="3"
                      @input="form.company_date_col = uppercaseColumn(form.company_date_col)"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group
                    label="Credit Column"
                    label-for="company-credit-col"
                  >
                    <b-form-input
                      id="company-credit-col"
                      v-model="form.company_credit_col"
                      required
                      placeholder="e.g., C"
                      maxlength="3"
                      @input="form.company_credit_col = uppercaseColumn(form.company_credit_col)"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group
                    label="Debit Column"
                    label-for="company-debit-col"
                  >
                    <b-form-input
                      id="company-debit-col"
                      v-model="form.company_debit_col"
                      required
                      placeholder="e.g., D"
                      maxlength="3"
                      @input="form.company_debit_col = uppercaseColumn(form.company_debit_col)"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
            </b-card>
          </b-col>

          <b-col md="6">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <h5 class="mb-0">
                VDPL (Vatika) Ledger
              </h5>
              <b-button
                v-if="vdplConfigChanged"
                variant="outline-primary"
                size="sm"
                :disabled="updatingVdplConfig"
                @click="updateVdplConfig"
              >
                <b-spinner
                  v-if="updatingVdplConfig"
                  small
                  class="mr-half"
                />
                Update Config
              </b-button>
            </div>
            <b-card class="mb-1 bg-light">
              <b-form-group
                label="Upload VDPL (Vatika) Ledger"
                label-for="my-ledger"
              >
                <b-form-file
                  id="my-ledger"
                  v-model="form.my_ledger"
                  accept=".xlsx,.xls"
                  placeholder="Choose Excel file or drop it here..."
                  drop-placeholder="Drop file here..."
                  required
                />
              </b-form-group>
              <b-row>
                <b-col md="6">
                  <b-form-group
                    label="Start Row"
                    label-for="my-start-row"
                  >
                    <b-form-input
                      id="my-start-row"
                      v-model.number="form.my_start_row"
                      type="number"
                      min="1"
                      required
                      placeholder="e.g., 8"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group
                    label="End Row"
                    label-for="my-end-row"
                  >
                    <b-form-input
                      id="my-end-row"
                      v-model.number="form.my_end_row"
                      type="number"
                      min="1"
                      required
                      placeholder="e.g., 200"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col md="6">
                  <b-form-group
                    label="Date Column"
                    label-for="my-date-col"
                  >
                    <b-form-input
                      id="my-date-col"
                      v-model="form.my_date_col"
                      required
                      placeholder="e.g., A"
                      maxlength="3"
                      @input="form.my_date_col = uppercaseColumn(form.my_date_col)"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group
                    label="Particulars Column"
                    label-for="my-particulars-col"
                  >
                    <b-form-input
                      id="my-particulars-col"
                      v-model="form.my_particulars_col"
                      required
                      placeholder="e.g., B"
                      maxlength="3"
                      @input="form.my_particulars_col = uppercaseColumn(form.my_particulars_col)"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col md="4">
                  <b-form-group
                    label="Debit Column"
                    label-for="my-debit-col"
                  >
                    <b-form-input
                      id="my-debit-col"
                      v-model="form.my_debit_col"
                      required
                      placeholder="e.g., F"
                      maxlength="3"
                      @input="form.my_debit_col = uppercaseColumn(form.my_debit_col)"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group
                    label="Credit Column"
                    label-for="my-credit-col"
                  >
                    <b-form-input
                      id="my-credit-col"
                      v-model="form.my_credit_col"
                      required
                      placeholder="e.g., G"
                      maxlength="3"
                      @input="form.my_credit_col = uppercaseColumn(form.my_credit_col)"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group
                    label="Balance Column"
                    label-for="my-balance-col"
                  >
                    <b-form-input
                      id="my-balance-col"
                      v-model="form.my_balance_col"
                      required
                      placeholder="e.g., H"
                      maxlength="3"
                      @input="form.my_balance_col = uppercaseColumn(form.my_balance_col)"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
            </b-card>
            <div class="w-100 d-flex">
              <b-button
                variant="primary"
                class="ml-auto"
                :disabled="submitting || !isFormValid"
                @click="handleSubmit"
              >
                <b-spinner
                  v-if="submitting"
                  small
                  class="mr-1"
                />
                {{ submitting ? 'Comparing...' : 'Compare Ledgers' }}
              </b-button>
            </div>
          </b-col>
        </b-row>
      </b-form>

      <!-- Results Section -->
      <compare-ledger-reports
        v-if="!submitting"
        :comparison-result="comparisonResult"
      />
    </b-card>

    <!-- Supplier Ledger Settings Modal -->
    <supplier-ledger-settings-modal
      v-if="showSupplierSettingsModal"
      @modal-closed="showSupplierSettingsModal = false"
    />
  </div>
</template>

<script>
import {
  BCard, BForm, BFormGroup, BFormInput, BFormFile,
  BRow, BCol, BButton, BAlert, BSpinner,
} from 'bootstrap-vue'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import CompareLedgerReports from '@/components/Accounts/CompareLedger/CompareLedgerReports.vue'
import CustomVSelect from '@/components/UI/CustomVSelect.vue'
import SupplierLedgerSettingsModal from '@/components/Accounts/CompareLedger/SupplierLedgerSettingsModal.vue'

export default {
  components: {
    BCard,
    BForm,
    BFormGroup,
    BFormInput,
    BFormFile,
    BRow,
    BCol,
    BButton,
    BAlert,
    BSpinner,
    CompareLedgerReports,
    CustomVSelect,
    SupplierLedgerSettingsModal,
  },
  data() {
    return {
      form: {
        company_ledger: null,
        company_start_row: null,
        company_end_row: null,
        company_credit_col: '',
        company_debit_col: '',
        company_start_col: '',
        company_end_col: '',
        company_date_col: '',
        my_ledger: null,
        my_start_row: 8,
        my_end_row: '',
        my_date_col: 'A',
        my_particulars_col: 'B',
        my_debit_col: 'F',
        my_credit_col: 'G',
        my_balance_col: 'H',
      },
      submitting: false,
      error: null,
      comparisonResult: null,

      // Supplier select
      selectedSupplier: null,
      supplierOptions: [],
      supplierOptionsLoading: false,

      // VDPL config - stores raw backend response for change detection
      vdplDefaults: null,
      updatingVdplConfig: false,

      // Modal
      showSupplierSettingsModal: false,
    }
  },
  computed: {
    isFormValid() {
      return this.form.company_ledger && this.form.my_ledger
    },
    vdplConfigChanged() {
      if (!this.vdplDefaults) return false
      // Compare form values against backend defaults (end_row excluded)
      return (
        String(this.form.my_start_row) !== String(this.vdplDefaults.start_row || '')
        || this.form.my_date_col !== (this.vdplDefaults.date_column || '')
        || this.form.my_particulars_col !== (this.vdplDefaults.particulars_column || '')
        || this.form.my_debit_col !== (this.vdplDefaults.debit_column || '')
        || this.form.my_credit_col !== (this.vdplDefaults.credit_column || '')
        || this.form.my_balance_col !== (this.vdplDefaults.balance_column || '')
      )
    },
  },
  created() {
    this.fetchSupplierOptions()
    this.fetchVdplSettings()
  },
  methods: {
    uppercaseColumn(value) {
      return value.toUpperCase().replace(/[^A-Z]/g, '')
    },
    cleanJsonString(jsonString) {
      return jsonString.replace(/:\s*NaN/g, ': null')
    },

    async fetchSupplierOptions() {
      this.supplierOptionsLoading = true
      try {
        const res = await axios.get('/supplier_options')
        this.supplierOptions = res.data.data
      } catch {
        this.supplierOptions = []
      } finally {
        this.supplierOptionsLoading = false
      }
    },

    async fetchVdplSettings() {
      try {
        const res = await axios.get('/vatika-ledger-settings/')
        const settings = res.data
        if (settings) {
          // Store raw backend response for change detection
          this.vdplDefaults = { ...settings }

          // Map backend keys → form keys (only overwrite if value is not null/empty)
          if (settings.start_row != null) this.form.my_start_row = settings.start_row
          if (settings.date_column) this.form.my_date_col = settings.date_column
          if (settings.particulars_column) this.form.my_particulars_col = settings.particulars_column
          if (settings.debit_column) this.form.my_debit_col = settings.debit_column
          if (settings.credit_column) this.form.my_credit_col = settings.credit_column
          if (settings.balance_column) this.form.my_balance_col = settings.balance_column
        }
      } catch {
        // Keep hardcoded defaults if fetch fails
      }
    },

    async handleSupplierChange(supplier) {
      if (!supplier) {
        this.form.company_start_row = null
        this.form.company_start_col = ''
        this.form.company_end_col = ''
        this.form.company_date_col = ''
        this.form.company_credit_col = ''
        this.form.company_debit_col = ''
        return
      }

      try {
        const res = await axios.get('/supplier-ledger-settings/', {
          params: { supplier: supplier.id },
        })
        const settings = Array.isArray(res.data) ? res.data : (res.data.results || [])
        if (settings.length > 0) {
          const config = settings[0]
          this.form.company_start_row = config.start_row || null
          this.form.company_start_col = config.start_column || ''
          this.form.company_end_col = config.end_column || ''
          this.form.company_date_col = config.date_column || ''
          this.form.company_credit_col = config.credit_column || ''
          this.form.company_debit_col = config.debit_column || ''
        } else {
          this.form.company_start_row = null
          this.form.company_start_col = ''
          this.form.company_end_col = ''
          this.form.company_date_col = ''
          this.form.company_credit_col = ''
          this.form.company_debit_col = ''
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'No Config Found',
              text: `No saved settings for ${supplier.text}. Please configure manually or add via Company Mapping.`,
              icon: 'InfoIcon',
              variant: 'warning',
            },
          })
        }
      } catch {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Failed to load config',
            text: 'Could not fetch supplier settings',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },

    async updateVdplConfig() {
      this.updatingVdplConfig = true
      try {
        // Send with backend keys (particulars_column, not perticulars_column)
        const payload = {
          start_row: this.form.my_start_row,
          date_column: this.form.my_date_col,
          particulars_column: this.form.my_particulars_col,
          debit_column: this.form.my_debit_col,
          credit_column: this.form.my_credit_col,
          balance_column: this.form.my_balance_col,
        }
        await axios.patch('/vatika-ledger-settings/', payload)
        // Update local defaults so button hides after save
        this.vdplDefaults = { ...this.vdplDefaults, ...payload }
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'VDPL Config Updated',
            text: 'Vatika ledger settings saved successfully',
            variant: 'success',
          },
        })
      } catch (err) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Update Failed',
            text: err?.response?.data?.error || err?.response?.data?.detail || 'Something went wrong',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.updatingVdplConfig = false
      }
    },

    async handleSubmit() {
      this.submitting = true
      this.error = null
      try {
        const formData = new FormData()
        formData.append('company_ledger', this.form.company_ledger)
        formData.append('my_ledger', this.form.my_ledger)
        formData.append('company_start_row', this.form.company_start_row)
        formData.append('company_end_row', this.form.company_end_row)
        formData.append('company_credit_col', this.form.company_credit_col)
        formData.append('company_debit_col', this.form.company_debit_col)
        formData.append('company_start_col', this.form.company_start_col)
        formData.append('company_end_col', this.form.company_end_col)
        formData.append('company_date_col', this.form.company_date_col)
        formData.append('my_start_row', this.form.my_start_row)
        formData.append('my_end_row', this.form.my_end_row)
        formData.append('my_date_col', this.form.my_date_col)
        formData.append('my_particulars_col', this.form.my_particulars_col)
        formData.append('my_debit_col', this.form.my_debit_col)
        formData.append('my_credit_col', this.form.my_credit_col)
        formData.append('my_balance_col', this.form.my_balance_col)

        const response = await axios.post('/accounts/compare-ledgers/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })

        let responseData = response.data
        if (typeof responseData === 'string') {
          try {
            const cleanedJson = this.cleanJsonString(responseData)
            responseData = JSON.parse(cleanedJson)
          } catch (parseError) {
            throw new Error('Invalid response format from server')
          }
        }
        if (responseData.status === 'success') {
          this.comparisonResult = responseData.result
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Success', text: 'Ledgers compared successfully!', icon: 'CheckCircleIcon', variant: 'success',
            },
          })
        }
      } catch (err) {
        this.error = err.response?.data?.message || err.response?.data?.error || 'Failed to compare ledgers'
        if (this.error.includes('index')) {
          this.error = `Please check configs (${err.response?.data?.error})`
        }
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error', text: this.error, icon: 'AlertCircleIcon', variant: 'danger',
          },
        })
      } finally {
        this.submitting = false
      }
    },
    resetForm() {
      this.form = {
        company_ledger: null,
        company_start_row: null,
        company_end_row: null,
        company_credit_col: '',
        company_debit_col: '',
        company_start_col: '',
        company_end_col: '',
        company_date_col: '',
        my_ledger: null,
        my_start_row: 8,
        my_end_row: null,
        my_date_col: 'A',
        my_particulars_col: 'B',
        my_debit_col: 'F',
        my_credit_col: 'G',
        my_balance_col: 'H',
      }
      this.selectedSupplier = null
      this.comparisonResult = null
      this.error = null
    },
  },
}
</script>

<style scoped>
.mr-half {
  margin-right: 0.25rem;
}
</style>
